
import { MouseDragInfo, ValidMouseDragInfo } from "@/lib/canvas/drag/drag";
import { UnitInputValue } from "@/types/unit";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import _throttle from "lodash/throttle";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import OxygenUnitInput from "@/oxygen/classes/inputs/OxygenUnitInput.vue";

import { Control } from "@/oxygen/classes/lib/types";
import { EasyEvent } from "@/util/easy-event";
import {
  isGlobalVariable,
  getGlobalVariableFromInputValue,
  getActiveElementOverrideVariables
} from "@/oxygen/variables/lib/utils";
import { uiStore } from "@/store/store";
import { DEFAULT_OXYGEN_POPOUT_WIDTH } from "@/lib/panels/panels";
import NiceButton from "@/components/UI/NiceButton.vue";

@Component({
  components: {
    NiceButton,
    OxygenControl: () => import("@/oxygen/classes/OxygenControl.vue"),
    OxygenUnitInput,
    Dropdown
  }
})
export default class MarginPaddingInputDragBar extends Vue {
  @Prop()
  value!: UnitInputValue;

  @Prop()
  type!: "margin" | "padding";

  @Prop()
  side!: "left" | "right" | "top" | "bottom";

  @Prop({ default: false })
  active!: boolean;

  open = false;
  mouseDragInfo: MouseDragInfo = false;
  numberWhenDragStarted: number | false = false;
  dropdownModifier = "";
  easyEvent = new EasyEvent();
  transitionKey: string | undefined = undefined;

  @Watch("open")
  onOpenChange() {
    this.updateMenuPosition();

    if (this.open) {
      uiStore.setPanelState({
        ...uiStore.panelState,
        oxyPanelIsMasked: true
      });

      this.$nextTick(() => {
        this.focusOnInput();
      });
    } else {
      uiStore.setPanelState({
        ...uiStore.panelState,
        oxyPanelIsMasked: false
      });
    }
  }

  @Watch("value")
  onValueChange() {
    this.updateMenuPosition();

    if (
      this.dropdownModifier === "center_horizontally" &&
      this.value?.unit !== "auto"
    ) {
      this.dropdownModifier = "";
    }
  }

  get dropdownUnitControl(): Control {
    return {
      type: "unit",
      name: `${this.type}-${this.side}`,
      slug: "unit"
    };
  }

  get displayValue() {
    if (this.isGlobalVariable) {
      return this.shorten(this.variableLabel);
    }

    if (this.value?.number === 0 || !this.value) {
      return "0";
    }

    if (this.value?.unit === "custom") {
      return this.shorten(this.value.number);
    }

    return this.shorten(this.value?.style);
  }

  get isGlobalVariable() {
    return isGlobalVariable(this.value);
  }

  get variable() {
    return getGlobalVariableFromInputValue(this.value);
  }

  get overrideVariable() {
    return getActiveElementOverrideVariables().find(
      v => v.id === this.variable?.id
    );
  }

  get variableLabel() {
    return this.overrideVariable
      ? this.overrideVariable.label
      : this.variable?.label;
  }

  get modifiers() {
    const presets = [
      { value: "apply_all", label: "Apply all (=)" },
      { value: "apply_opposite", label: "Apply opposite" },
      {
        value: "center_horizontally",
        label: "Center horizontally",
        type: "margin",
        sides: ["left", "right"]
      }
    ];

    return presets.filter(({ type, sides }) => {
      if (type && type !== this.type) return false;
      if (sides && !sides.includes(this.side)) return false;
      return true;
    });
  }

  get width() {
    return DEFAULT_OXYGEN_POPOUT_WIDTH;
  }

  get height() {
    return 43;
  }

  get classList() {
    const isHorizontal = this.side === "left" || this.side === "right";

    return {
      "margin-padding-input-drag-bar": true,
      [`oxy-drag-cursor oxy-drag-cursor-${this.type}-${this.side}`]: true,
      [`${this.type}-${this.side}-drag-bar`]: true,
      "margin-padding-input-drag-bar-horizontal": isHorizontal,
      "margin-padding-input-drag-bar--active": this.active
    };
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  dragCallback(mouseDragInfo: ValidMouseDragInfo, e: MouseEvent) {
    if (this.numberWhenDragStarted === false) return;

    const isVertical = this.side === "top" || this.side === "bottom";

    let changeAmount = isVertical
      ? mouseDragInfo.totalYChange
      : mouseDragInfo.totalXChange;

    const isReversed =
      (this.type === "padding" &&
        (this.side === "right" || this.side === "bottom")) ||
      (this.type === "margin" && (this.side === "left" || this.side === "top"));

    if (isReversed) {
      changeAmount *= -1;
    }

    const unitType = this.value?.unit ? this.value.unit : "px";

    let newNumber;

    if (["em"].includes(unitType)) {
      changeAmount = changeAmount / 10;
      // preceding + trick: https://stackoverflow.com/a/12830454
      newNumber = Number(
        +(this.numberWhenDragStarted + changeAmount).toFixed(2)
      );
    } else {
      newNumber = +Number(this.numberWhenDragStarted + changeAmount).toFixed(0);
    }

    const newValue: UnitInputValue = {
      unit: unitType,
      number: newNumber,
      style: `${newNumber}${unitType}`
    };

    this.$emit("input", {
      value: newValue,
      modifierKey: e.altKey ? "alt" : e.shiftKey ? "shift" : undefined,
      transactionKey: this.transitionKey
    });
  }

  handleDoubleClick(e: MouseEvent) {
    this.$emit("input", {
      value: null,
      modifierKey: e.altKey ? "alt" : e.shiftKey ? "shift" : undefined
    });
  }

  emit(value: UnitInputValue) {
    this.$emit("input", {
      value: value
    });
  }

  dragStart(e: MouseEvent) {
    e.stopPropagation();

    this.transitionKey = `PropertyChange:Spacing:${
      this.elementId
    }:${Date.now()}`;

    this.mouseDragInfo = {
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      lastXChange: 0,
      lastYChange: 0,
      totalXChange: 0,
      totalYChange: 0
    };

    this.$emit("dragging", true);

    this.easyEvent.on("mousemove", this.throttledDragging);
    this.easyEvent.on("mouseup", this.dragEnd);

    this.numberWhenDragStarted = Number.isNaN(Number(this.value?.number))
      ? 0
      : Number(this.value?.number);

    document.body.classList.add(`oxy-drag-global-cursor-${this.side}`);
  }

  throttledDragging = _throttle(this.dragging, 60);

  dragging(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    requestAnimationFrame(() => this._draggingFunc(e));
  }

  dragEnd(e: MouseEvent) {
    e.stopPropagation();

    this.easyEvent.off("mousemove", this.throttledDragging);
    this.easyEvent.off("mouseup", this.dragEnd);

    this.transitionKey = undefined;
    this.open = false;
    this.mouseDragInfo = false;
    this.numberWhenDragStarted = false;

    const side = this.side;
    document.body.classList.remove(`oxy-drag-global-cursor-${side}`);

    this.$emit("dragging", false);
  }

  _draggingFunc(e: MouseEvent) {
    if (!this.mouseDragInfo) return;

    const currentX = e.clientX;
    const currentY = e.clientY;
    const totalXChange = currentX - this.mouseDragInfo.startX;
    const totalYChange = currentY - this.mouseDragInfo.startY;

    const threshold = 1; // pixels
    const hasPassedThreshold =
      Math.abs(totalXChange) > threshold || Math.abs(totalYChange) > threshold;

    if (!hasPassedThreshold) return;

    this.mouseDragInfo = {
      ...this.mouseDragInfo,
      currentX,
      currentY,
      lastXChange: currentX - this.mouseDragInfo.currentX,
      lastYChange: currentY - this.mouseDragInfo.currentY,
      totalXChange,
      totalYChange
    };

    this.dragCallback(this.mouseDragInfo, e);
  }

  shorten(maybeStr?: string | number | null) {
    const str = `${maybeStr || ""}`;

    const sides = {
      margin: {
        top: 30,
        bottom: 30,
        left: 5,
        right: 5
      },
      padding: {
        top: 6,
        bottom: 16,
        left: 5,
        right: 5
      }
    };

    const maxLength = sides[this.type][this.side];
    const minLengthToTruncate = 5;
    const maxLengthToTruncate = 10;

    if (
      maxLength <= maxLengthToTruncate &&
      maxLength >= minLengthToTruncate &&
      str.length > minLengthToTruncate
    ) {
      return `${str.slice(0, maxLength)}...`;
    }

    if (str.length <= maxLength) {
      return str;
    }

    const partLength = Math.floor((maxLength - 3) / 2); // Divide the available space for both parts
    const start = str.substring(0, partLength);
    const end = str.substring(str.length - partLength);

    return `${start}...${end}`;
  }

  menuPosition = { x: 0, y: 0 };

  updateMenuPosition() {
    const activator = this.$refs.activator as HTMLElement;
    const activatorRect = activator.getBoundingClientRect();

    this.menuPosition.x =
      activatorRect.x + (activatorRect.width / 2 - this.width / 2);
    this.menuPosition.y =
      activatorRect.top + (activatorRect.height / 2 - this.height / 2);
  }

  focusOnInput() {
    const container = this.$refs.container as HTMLElement;

    // Vuetify is doing some weird stuff with the focus
    setTimeout(() => {
      const input = container.querySelector("input");
      if (input) input.focus();
    }, 100);
  }

  get dropdownModifierKey() {
    if (this.dropdownModifier === "apply_all") {
      return "shift";
    } else if (this.dropdownModifier === "apply_opposite") {
      return "alt";
    } else if (this.dropdownModifier === "center_horizontally") {
      return "alt";
    }
    return undefined;
  }

  setDropdownModifier(modifier: string) {
    // Toggle off
    if (modifier === this.dropdownModifier) {
      this.dropdownModifier = "";
      return;
    } else {
      this.dropdownModifier = modifier;
    }

    const autoValue: UnitInputValue = {
      unit: "auto",
      number: "",
      style: "auto"
    };

    const value =
      this.dropdownModifier === "center_horizontally" ? autoValue : this.value;

    this.$emit("input", {
      value,
      modifierKey: this.dropdownModifierKey
    });
  }

  maybeToggleAll(event: KeyboardEvent) {
    if (event.key !== "=") return;

    const container = this.$refs.container as HTMLElement;

    if (!container) return;
    if (!container.contains(document.activeElement)) return;

    event.preventDefault();

    this.setDropdownModifier("apply_all");
  }

  mounted() {
    addEventListener("keydown", this.maybeToggleAll);
  }

  beforeDestroy() {
    removeEventListener("keydown", this.maybeToggleAll);
  }
}
